// TopDonors.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Button, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../../../assets/css/home/FeaturedDonation.css';

const FeaturedDonation = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [counters, setCounters] = useState({});

  const topDonors = [
    {
      id: 1,
      name: 'Ahmed Khan',
      rank: 1,
      badge: 'Gold',
      totalDonated: 125000,
      donations: 50,
      avatar: 'AK',
      verified: true,
      impact: 500,
      color: '#FFD700',
      bgGradient: 'linear-gradient(135deg, #FFD700, #FFA500)'
    },
    {
      id: 2,
      name: 'Sara Ali',
      rank: 2,
      badge: 'Gold',
      totalDonated: 110000,
      donations: 45,
      avatar: 'SA',
      verified: true,
      impact: 450,
      color: '#FFD700',
      bgGradient: 'linear-gradient(135deg, #C0C0C0, #A8A8A8)'
    },
    {
      id: 3,
      name: 'Fatima Hassan',
      rank: 3,
      badge: 'Gold',
      totalDonated: 105000,
      donations: 42,
      avatar: 'FH',
      verified: true,
      impact: 420,
      color: '#CD7F32',
      bgGradient: 'linear-gradient(135deg, #CD7F32, #8B4513)'
    },
    {
      id: 4,
      name: 'Ali Raza',
      rank: 4,
      badge: 'Silver',
      totalDonated: 80000,
      donations: 35,
      avatar: 'AR',
      verified: true,
      impact: 350,
      color: '#C0C0C0',
      bgGradient: 'linear-gradient(135deg, #E0E0E0, #C0C0C0)'
    },
    {
      id: 5,
      name: 'Zainab Ahmed',
      rank: 5,
      badge: 'Silver',
      totalDonated: 75000,
      donations: 32,
      avatar: 'ZA',
      verified: true,
      impact: 320,
      color: '#C0C0C0',
      bgGradient: 'linear-gradient(135deg, #E0E0E0, #C0C0C0)'
    },
    {
      id: 6,
      name: 'Hassan Ali',
      rank: 6,
      badge: 'Bronze',
      totalDonated: 50000,
      donations: 25,
      avatar: 'HA',
      verified: false,
      impact: 250,
      color: '#CD7F32',
      bgGradient: 'linear-gradient(135deg, #D4AF37, #CD7F32)'
    }
  ];

  const topNGOs = [
    {
      id: 1,
      name: 'Hope Foundation',
      campaigns: 15,
      raised: 450000,
      rating: 4.9,
      verified: true,
      members: 234,
      image: 'HF'
    },
    {
      id: 2,
      name: 'Care International',
      campaigns: 12,
      raised: 380000,
      rating: 4.8,
      verified: true,
      members: 198,
      image: 'CI'
    },
    {
      id: 3,
      name: 'Help Society',
      campaigns: 10,
      raised: 320000,
      rating: 4.7,
      verified: true,
      members: 156,
      image: 'HS'
    }
  ];

  // Initialize particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const container = canvas.parentElement;
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;

    const particleCount = 100;
    const newParticles = [];

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        color: '#FFB800',
        originalColor: '#FFB800'
      });
    }

    setParticles(newParticles);

    const handleResize = () => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse move handler
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => canvas.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animate particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, i) => {
        const dx = mousePos.x - particle.x;
        const dy = mousePos.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150 && hoveredCard !== null) {
          particle.vx += dx * 0.0001;
          particle.vy += dy * 0.0001;
          particle.color = topDonors[hoveredCard]?.color || '#FFB800';
        } else {
          particle.color = particle.originalColor;
        }

        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();

        particles.slice(i + 1).forEach((otherParticle) => {
          const dx2 = particle.x - otherParticle.x;
          const dy2 = particle.y - otherParticle.y;
          const distance2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          if (distance2 < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(255, 184, 0, ${1 - distance2 / 100})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationFrameId);
  }, [particles, mousePos, hoveredCard]);

  // Counter animation
  useEffect(() => {
    const animateCounter = (id, target) => {
      let current = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => ({ ...prev, [id]: Math.floor(current) }));
      }, 30);
    };

    topDonors.forEach(donor => {
      animateCounter(`donor-${donor.id}`, donor.totalDonated);
    });
  }, []);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRankClass = (rank) => {
    if (rank <= 3) return 'top-three';
    return '';
  };

  return (
    <div className="top-donors-section">
      <canvas ref={canvasRef} className="top-donors-particle-canvas"></canvas>

      {/* ✅ FIX 1: fluid="lg" add kiya taake container properly responsive ho */}
      <Container fluid="lg" className="top-donors-content-container">

        {/* Top Donors Section */}
        <div className="top-donors-section-header text-center mb-5">
          <h2 className="top-donors-section-title">
            <span className="top-donors-trophy-icon">🏆</span>
            TOP DONORS
            <span className="top-donors-trophy-icon">🏆</span>
          </h2>
          <p className="top-donors-section-subtitle">Our Amazing Contributors Making a Difference</p>
        </div>

        <Row className="mb-5 g-4">
          {topDonors.map((donor, index) => (
            <Col xl={4} lg={6} md={6} sm={12} key={donor.id}>
              <Card
                className={`top-donors-donor-card ${getRankClass(donor.rank)} h-100`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setSelectedDonor(donor)}
                style={{ '--card-delay': `${index * 0.1}s` }}
              >
                <Card.Body className="d-flex flex-column">
                  {/* Rank Badge */}
                  <div className="top-donors-rank-badge" style={{ background: donor.bgGradient }}>
                    <span className="top-donors-rank-icon">{getRankIcon(donor.rank)}</span>
                  </div>

                  {/* Avatar */}
                  <div className="top-donors-donor-avatar-wrapper">
                    <div
                      className="top-donors-donor-avatar"
                      style={{ borderColor: donor.color }}
                    >
                      <span className="top-donors-avatar-text">{donor.avatar}</span>
                    </div>
                    {donor.verified && (
                      <div className="top-donors-verified-badge">
                        <i className="bi bi-check-circle-fill"></i>
                      </div>
                    )}
                  </div>

                  {/* Donor Info */}
                  <h5 className="top-donors-donor-name">{donor.name}</h5>
                  <Badge
                    bg="warning"
                    className="top-donors-donor-badge mb-3"
                    style={{ backgroundColor: donor.color }}
                  >
                    {donor.badge} Donor
                  </Badge>

                  {/* Stats */}
                  <div className="top-donors-donor-stats mb-3">
                    <div className="top-donors-stat-item">
                      <span className="top-donors-stat-label">Total Donated</span>
                      <span className="top-donors-stat-value">
                        Rs {(counters[`donor-${donor.id}`] || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="top-donors-stat-divider"></div>
                    <div className="top-donors-stat-item">
                      <span className="top-donors-stat-label">Donations</span>
                      <span className="top-donors-stat-value">{donor.donations}</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">Impact Score</small>
                      <small className="text-muted">{donor.impact}/500</small>
                    </div>
                    <ProgressBar
                      now={(donor.impact / 500) * 100}
                      className="top-donors-custom-progress"
                      style={{
                        '--progress-color': donor.color,
                        height: '8px'
                      }}
                    />
                  </div>

                  {/* ✅ FIX 3: mt-auto add kiya taake button hamesha bottom mein rahe */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 top-donors-view-profile-btn mt-auto"
                    style={{
                      borderColor: '#165C5C',
                      color: '#165C5C'
                    }}
                  >
                    View Profile
                  </Button>
                </Card.Body>

                {/* Glow Effect for Top 3 */}
                {donor.rank <= 3 && (
                  <div
                    className="top-donors-card-glow"
                    style={{ background: donor.color }}
                  ></div>
                )}
              </Card>
            </Col>
          ))}
        </Row>

        {/* Top NGOs Section */}
        <div className="top-donors-ngo-section mt-5">
          <div className="top-donors-section-header text-center mb-5">
            <h2 className="top-donors-section-title">
              <span className="top-donors-trophy-icon">⭐</span>
              VERIFIED NGOs
              <span className="top-donors-trophy-icon">⭐</span>
            </h2>
            <p className="top-donors-section-subtitle">Trusted Organizations Transforming Lives</p>
          </div>

          {/* ✅ FIX 4: g-4 gutter add kiya NGO row mein bhi */}
          <Row className="g-4">
            {topNGOs.map((ngo, index) => (
              <Col lg={4} md={6} sm={12} key={ngo.id}>
                <Card
                  className="top-donors-ngo-card h-100"
                  style={{ '--card-delay': `${index * 0.1}s` }}
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="top-donors-ngo-header d-flex justify-content-between align-items-start mb-3">
                      <div className="top-donors-ngo-avatar">
                        <span>{ngo.image}</span>
                      </div>
                      {ngo.verified && (
                        <Badge bg="success" className="top-donors-verified-badge-ngo">
                          <i className="bi bi-check-circle-fill me-1"></i>
                          Verified
                        </Badge>
                      )}
                    </div>

                    <h5 className="top-donors-ngo-name mb-3">{ngo.name}</h5>

                    <div className="top-donors-ngo-stats mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-clipboard-check me-2 text-primary"></i>
                        <span>{ngo.campaigns} Active Campaigns</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-currency-dollar me-2 text-success"></i>
                        <span>Rs {ngo.raised.toLocaleString()} Raised</span>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-people me-2 text-info"></i>
                        <span>{ngo.members} Members</span>
                      </div>
                    </div>

                    <div className="top-donors-ngo-rating mb-3">
                      <div className="top-donors-stars">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`bi bi-star${i < Math.floor(ngo.rating) ? '-fill' : ''}`}
                            style={{ color: '#FFB800' }}
                          ></i>
                        ))}
                      </div>
                      <span className="top-donors-rating-value ms-2">{ngo.rating}</span>
                    </div>

                    {/* ✅ FIX 5: mt-auto taake NGO button bhi hamesha bottom mein rahe */}
                    <Button
                      variant="primary"
                      className="w-100 top-donors-ngo-view-btn mt-auto"
                      onClick={() => navigate(`/ngo/${ngo.id}`)}
                    >
                      View Profile
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default FeaturedDonation;